import { useEffect, useRef } from 'react';
import {
  isCompleteForApplicant,
  isHubStep,
  isNavigationComplete,
  needsNavigationAttention,
  type NormalStep,
  type PhaseSection,
} from '../lib/steps';

export interface ApplicationStepperNavProps {
  phases: PhaseSection[];
  phase: PhaseSection | undefined;
  step: NormalStep | undefined;
  steps: NormalStep[];
  progressPct: number;
  onSelectPhase: (phaseKey: string) => void;
  onSelectStep: (stepKey: string) => void;
}

/**
 * Desktop header + phase tabs + numbered stepper on top of the form (Nexus
 * lifecycle contract): phase tabs and step circles each render as a single
 * horizontal row that scrolls past 6 phases / 7 steps rather than wrapping or
 * truncating. Mobile uses MobileApplicationNav instead — this component is
 * desktop-only, matching the isMobile branch it was extracted from in
 * ApplicationPage.
 */
export default function ApplicationStepperNav({
  phases,
  phase,
  step,
  steps,
  progressPct,
  onSelectPhase,
  onSelectStep,
}: ApplicationStepperNavProps) {
  // Over-cap scroll anchor (Nexus applicant-lifecycle contract): step and
  // phase rows never wrap or truncate — past the 7-step / 6-phase caps they
  // scroll horizontally, and on render the applicant lands looking at their
  // ACTIVE step and phase, not at step 1.
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const activeStepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
    activeStepRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [phase?.[0], step?.stepKey]);
  const activePhaseSteps = phase
    ? (([, phaseDetails]) => phaseDetails.steps)(phase)
    : [];

  return (
    <>
      <header className="pagehead">
        <div>
          <h1>My application</h1>
        </div>
        <div className="progressring" aria-label={`${progressPct}% complete`}>
          {progressPct}%
        </div>
      </header>

      <div className="phasetabs" role="tablist">
        {phases.map(([k, p]) => {
          const d = p.steps.filter((candidate) => isNavigationComplete(candidate, steps)).length;
          return (
            <button key={k} role="tab" aria-selected={phase?.[0] === k}
              ref={phase?.[0] === k ? activeTabRef : undefined}
              className={`tab ${phase?.[0] === k ? 'active' : ''}`}
              onClick={() => onSelectPhase(k)}>
              {p.name}
              <span className="tabcount">{d}/{p.steps.length}</span>
            </button>
          );
        })}
      </div>

      {/* Numbered stepper on top of the form (Nexus lifecycle contract):
          single horizontal row, scrolls past 7 steps, active circle pulses. */}
      <ol className="stepper" aria-label="Application steps">
        {activePhaseSteps.map((s, i) => {
          const isActive = s.stepKey === step?.stepKey;
          const attention = needsNavigationAttention(s, steps);
          const stepDone = isNavigationComplete(s, steps);
          const previousStep = i > 0 ? activePhaseSteps.at(i - 1) : undefined;
          const prevDone = previousStep ? isNavigationComplete(previousStep, steps) : false;
          return (
            <li key={s.stepKey} className={prevDone ? 'lineon' : ''}>
              <button
                ref={isActive ? activeStepRef : undefined}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${s.title}: ${attention ? 'changes requested' : stepDone ? 'complete' : 'incomplete'}`}
                className={`stepdot${isActive ? ' active' : ''}${
                  stepDone ? ' done' : ''
                }${attention ? ' err' : ''}`}
                onClick={() => onSelectStep(s.stepKey)}
              >
                <span className="circle" aria-hidden>
                  {attention
                    ? '!'
                    : isHubStep(s)
                    ? (() => {
                        // Hub badge: x/y sub-tasks complete (AC2)
                        const subKeys = s.hubSubtaskMap ?? [];
                        const subDone = subKeys.filter((k) => {
                          const subtask = steps.find((candidate) => candidate.stepKey === k);
                          return subtask ? isCompleteForApplicant(subtask) : false;
                        }).length;
                        const allDone = subDone === subKeys.length && subKeys.length > 0;
                        return allDone ? '✓' : `${subDone}/${subKeys.length}`;
                      })()
                    : stepDone ? '✓' : i + 1}
                </span>
                <span className="lbl">{s.title}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </>
  );
}
